/**
 * Custom hook for Firebase Phone Authentication
 * Uses Firebase SDK directly with Emulator support for local development
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { auth, RecaptchaVerifier, signInWithPhoneNumber } from '../firebase'; // Assuming 'auth' and 'RecaptchaVerifier' are exported from firebase.js

export const useFirebasePhoneAuth = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [verificationId, setVerificationId] = useState(null);
  const confirmationResultRef = useRef(null);
  const recaptchaVerifierRef = useRef(null);

  /**
   * Initialize reCAPTCHA verifier
   * Skip completely in development mode (if appVerificationDisabledForTesting is true)
   */
  const initializeRecaptcha = useCallback((containerId, invisible = false) => {
    try {
      // Clear existing verifier
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null; // Ensure it's null after clearing
      }

      // Check if Firebase Auth is configured to skip reCAPTCHA for testing
      // This is set in firebase.js based on the development environment
      if (auth.settings.appVerificationDisabledForTesting) {
        console.log('[FirebaseAuth] Development mode: skipping reCAPTCHA initialization');
        return null; // Explicitly return null, Firebase will handle the absence of a verifier
      }

      // Check if container exists for production/non-dev environments
      const container = document.getElementById(containerId);
      if (!container) {
        console.error('[FirebaseAuth] reCAPTCHA container not found:', containerId);
        setError('reCAPTCHA container not found');
        return null;
      }

      // Initialize RecaptchaVerifier for non-development environments
      recaptchaVerifierRef.current = new RecaptchaVerifier(auth, containerId, {
        size: invisible ? 'invisible' : 'normal',
        callback: () => {
          // You might add logic here for when reCAPTCHA is successfully resolved
          console.log('[FirebaseAuth] reCAPTCHA solved.');
        },
        'expired-callback': () => {
          console.warn('[FirebaseAuth] reCAPTCHA expired');
          setError('reCAPTCHA expired. Please try again.');
          // Optionally re-render or re-initialize if it was a visible reCAPTCHA
          // recaptchaVerifierRef.current.render();
        },
        'error-callback': (err) => {
          console.error('[FirebaseAuth] reCAPTCHA error during callback:', err);
          setError('reCAPTCHA verification failed. Please try again.');
        },
      });

      // Render the reCAPTCHA (even if invisible, it needs to be rendered to initialize its state)
      // This .render() returns a promise resolving with the widget ID
      recaptchaVerifierRef.current.render().catch((err) => {
        console.error('[FirebaseAuth] reCAPTCHA render error:', err);
        setError('Failed to render reCAPTCHA. Please try reloading.');
        recaptchaVerifierRef.current = null; // Clear if rendering failed
      });

      return recaptchaVerifierRef.current;
    } catch (err) {
      console.error('[FirebaseAuth] reCAPTCHA initialization error:', err);
      setError('Failed to initialize reCAPTCHA');
      return null;
    }
  }, []); // Dependencies for useCallback. `auth` is stable.

  /**
   * Send OTP to phone number
   * @param {string} phoneNumber - Phone number
   * @param {string} recaptchaContainerId - ID of reCAPTCHA container
   */
  const sendOTP = useCallback(
    async (phoneNumber, recaptchaContainerId = 'recaptcha-container') => {
      setLoading(true);
      setError(null);

      try {
        // Ensure phone number is in E.164 format
        const formattedPhone = phoneNumber.startsWith('+') ? phoneNumber : `+91${phoneNumber}`;

        // Initialize reCAPTCHA (will return null if appVerificationDisabledForTesting is true)
        const recaptchaVerifier = initializeRecaptcha(recaptchaContainerId, true);

        let confirmationResult;
        // --- START OF MODIFICATION ---
        // The key change: Conditionally pass the recaptchaVerifier argument.
        // If recaptchaVerifier is null (in dev mode, when appVerificationDisabledForTesting is true),
        // we omit the third argument entirely as per Firebase SDK's expectation.
        if (recaptchaVerifier) {
          console.log('[FirebaseAuth] Sending OTP with reCAPTCHA verifier.');
          confirmationResult = await signInWithPhoneNumber(auth, formattedPhone, recaptchaVerifier);
        } else {
          // This path is taken in development environments where reCAPTCHA is skipped.
          // Firebase SDK expects the verifier argument to be omitted, not 'null'.
          console.log(
            '[FirebaseAuth] Sending OTP without explicit reCAPTCHA verifier (dev mode/testing).'
          );
          confirmationResult = await signInWithPhoneNumber(auth, formattedPhone);
        }
        // --- END OF MODIFICATION ---

        confirmationResultRef.current = confirmationResult;
        setVerificationId(confirmationResult.verificationId);
        setLoading(false);

        return {
          success: true,
          verificationId: confirmationResult.verificationId,
        };
      } catch (err) {
        console.error('[FirebaseAuth] Send OTP error:', err);
        setLoading(false);

        let errorMessage = 'Failed to send OTP. Please try again.';
        if (err.code === 'auth/invalid-phone-number') {
          errorMessage =
            'Invalid phone number format. Please ensure it includes the country code (e.g., +11234567890).';
        } else if (err.code === 'auth/too-many-requests') {
          errorMessage = 'Too many requests. Please try again later to prevent abuse.';
        } else if (err.code === 'auth/captcha-check-failed') {
          errorMessage = 'reCAPTCHA verification failed. Please try again.';
        } else if (err.code === 'auth/argument-error') {
          // This error was the primary one we were debugging.
          // If it still appears here, it's highly unexpected and points to an SDK internal issue.
          errorMessage =
            'An internal authentication argument error occurred. Please try again or contact support.';
        } else if (err.code === 'auth/app-not-authorized') {
          errorMessage =
            'This app is not authorized to use Firebase Authentication. Please check domain settings in Firebase console.';
        } else if (err.code === 'auth/network-request-failed') {
          errorMessage = 'Network error. Please check your internet connection and try again.';
        }

        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [initializeRecaptcha]
  ); // Dependency: initializeRecaptcha itself (which is useCallback memoized)

  /**
   * Verify OTP code
   * @param {string} otpCode - 6-digit OTP code
   */
  const verifyOTP = useCallback(async (otpCode) => {
    setLoading(true);
    setError(null);

    try {
      if (!confirmationResultRef.current) {
        throw new Error('No verification in progress. Please request OTP first.');
      }

      const result = await confirmationResultRef.current.confirm(otpCode);
      const user = result.user;

      // Get Firebase ID token
      const idToken = await user.getIdToken();

      setLoading(false);

      return {
        success: true,
        user: {
          uid: user.uid,
          phoneNumber: user.phoneNumber,
          idToken,
        },
      };
    } catch (err) {
      console.error('[FirebaseAuth] Verify OTP error:', err);
      setLoading(false);

      let errorMessage = 'Invalid OTP. Please try again.';
      if (err.code === 'auth/invalid-verification-code') {
        errorMessage = 'The provided OTP code is invalid or has expired.';
      } else if (err.code === 'auth/code-expired') {
        errorMessage = 'The OTP has expired. Please request a new one.';
      } else if (err.code === 'auth/missing-verification-code') {
        errorMessage = 'Please enter the verification code.';
      }

      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Reset auth state
   */
  const resetAuth = useCallback(() => {
    setVerificationId(null);
    confirmationResultRef.current = null;
    setError(null);
    setLoading(false);

    // Clear reCAPTCHA
    if (recaptchaVerifierRef.current) {
      try {
        recaptchaVerifierRef.current.clear();
        // Set to null after clearing to ensure it's re-initialized if needed later
        recaptchaVerifierRef.current = null;
      } catch (err) {
        console.error('[FirebaseAuth] Error clearing reCAPTCHA:', err);
      }
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (err) {
          console.error('[FirebaseAuth] Cleanup error:', err);
        }
      }
    };
  }, []);

  return {
    loading,
    error,
    verificationId,
    sendOTP,
    verifyOTP,
    clearError,
    resetAuth,
  };
};
